import {
  Film, Gift, Handshake, RollerCoaster, Tent, Ticket,
} from "lucide-react";

import promotionCardAmazon from "@/assets/images/shop/promotion_card_amazon.webp";
import promotionCardAppleTV from "@/assets/images/shop/promotion_card_appletv.webp";
import promotionCardAsterix from "@/assets/images/shop/promotion_card_asterix.webp";
import promotionCardCarrefour from "@/assets/images/shop/promotion_card_carrefour.webp";
import promotionCardDisneyland from "@/assets/images/shop/promotion_card_disneyland.webp";
import promotionCardDisneyPlus from "@/assets/images/shop/promotion_card_disneyplus.webp";
import promotionCardFnac from "@/assets/images/shop/promotion_card_fnac.webp";
import promotionCardIkea from "@/assets/images/shop/promotion_card_ikea.webp";
import promotionCardMonoprix from "@/assets/images/shop/promotion_card_monoprix.webp";
import promotionCardPathe from "@/assets/images/shop/promotion_card_pathe.webp";
import promotionCardSephora from "@/assets/images/shop/promotion_card_sephora.webp";
import promotionCardSpotify from "@/assets/images/shop/promotion_card_spotify.webp";
import {
  CADEAUX_PATH,
  CINEMA_PATH,
  PARCS_LOISIRS_PATH,
  CAMPING_PATH,
  TICKETING_PATH,
  PARTNERS_PATH,
} from "@/features/boutique/routes";
import type { AutoScrollCardItem } from "@/features/shop/types/autoScrollSlider.types";

export const SHOP_PAGE_CONFIG = {
  BANNER_SLIDER_AUTOPLAY_INTERVAL: 8000,
  AUTO_SCROLL_SLIDER_SPEED: 0.25,
  ALT_AUTO_SCROLL_SLIDER_SPEED: 0.35,
} as const;

export const TAG_COLORS = {
  TICKET: {
    backgroundColor: "bg-blue-500",
    textColor: "text-white",
  },
  CAMPING: {
    backgroundColor: "bg-green-400",
    textColor: "text-white",
  },
  GIFT: {
    backgroundColor: "bg-pink-300",
    textColor: "text-white",
  },
  CINEMA: {
    backgroundColor: "bg-orange-400",
    textColor: "text-white",
  },
  PARK: {
    backgroundColor: "bg-indigo-400",
    textColor: "text-white",
  },
  PARTNER: {
    backgroundColor: "bg-amber-500",
    textColor: "text-white",
  },
  DISCOUNT: {
    backgroundColor: "bg-emerald-400",
    textColor: "text-white",
  },
} as const;

export const SHOP_CATEGORIES_CARDS = [
  {
    key: "gifts",
    title: "Bons cadeaux",
    icon: Gift,
    backgroundColor: "bg-pink-400/10 dark:bg-pink-400/20",
    textColor: "text-pink-400 dark:text-pink-300",
    link: `${CADEAUX_PATH}?drawer=boutique`,
    featureFlag: "catalog_gifts" as const,
  },
  {
    key: "cinemas",
    title: "Cinémas",
    icon: Film,
    backgroundColor: "bg-orange-400/10 dark:bg-orange-400/20",
    textColor: "text-orange-400 dark:text-orange-300",
    link: `${CINEMA_PATH}?drawer=boutique`,
    featureFlag: "catalog_cinemas" as const,
  },
  {
    key: "parcs",
    title: "Parcs de loisirs",
    icon: RollerCoaster,
    backgroundColor: "bg-indigo-400/10 dark:bg-indigo-400/20",
    textColor: "text-indigo-400 dark:text-indigo-300",
    link: `${PARCS_LOISIRS_PATH}?drawer=boutique`,
    featureFlag: "catalog_parks" as const,
  },
  {
    key: "campings",
    title: "Campings",
    icon: Tent,
    backgroundColor: "bg-green-400/10 dark:bg-green-400/20",
    textColor: "text-green-400 dark:text-green-300",
    link: `${CAMPING_PATH}?drawer=boutique`,
    featureFlag: "catalog_campings" as const,
  },
  {
    key: "billetterie",
    title: "Billetterie",
    icon: Ticket,
    backgroundColor: "bg-purple-400/10 dark:bg-purple-400/20",
    textColor: "text-purple-400 dark:text-purple-300",
    link: `${TICKETING_PATH}?drawer=boutique`,
    featureFlag: "catalog_tickets" as const,
  },
  {
    key: "partenaires",
    title: "Partenaires",
    icon: Handshake,
    backgroundColor: "bg-amber-400/10 dark:bg-amber-400/20",
    textColor: "text-amber-400 dark:text-amber-300",
    link: `${PARTNERS_PATH}?drawer=boutique`,
    featureFlag: null,
  },
];

export const SHOP_AUTO_SCROLL_CARDS: AutoScrollCardItem[] = [
  {
    id: "amazon",
    title: "Cartes Amazon",
    subtitle: "Tout ce dont vous avez besoin, livré rapidement",
    backgroundColor: "bg-orange-600",
    image: promotionCardAmazon,
    imageBackground: "bg-orange-500/30",
    link: "/boutique",
  },
  {
    id: "appletv",
    title: "Apple TV+",
    subtitle: "Des séries et films exclusifs en streaming",
    backgroundColor: "bg-black",
    image: promotionCardAppleTV,
    imageBackground: "bg-zinc-500/30",
    link: "/boutique",
  },
  {
    id: "asterix",
    title: "Parc Astérix",
    subtitle: "Vivez l'aventure gauloise en famille",
    backgroundColor: "bg-green-800",
    image: promotionCardAsterix,
    imageBackground: "bg-green-500/30",
    link: "/boutique",
  },
  {
    id: "disneyland",
    title: "Disneyland Paris",
    subtitle: "Vivez la magie de Disney à prix réduit",
    backgroundColor: "bg-violet-800",
    image: promotionCardDisneyland,
    imageBackground: "bg-pink-500/30",
    link: "/boutique",
  },
  {
    id: "disneyplus",
    title: "Disney+",
    subtitle: "Tous vos films et séries Disney préférés",
    backgroundColor: "bg-cyan-600",
    image: promotionCardDisneyPlus,
    imageBackground: "bg-blue-400/30",
    link: "/boutique",
  },
  {
    id: "spotify",
    title: "Cartes Spotify",
    subtitle: "Écoutez votre musique sans limite",
    backgroundColor: "bg-green-600",
    image: promotionCardSpotify,
    imageBackground: "bg-green-500/30",
    link: "/boutique",
  },
];

export const ALT_SHOP_AUTO_SCROLL_CARDS: AutoScrollCardItem[] = [
  {
    id: "pathe",
    title: "Cinéma Pathé",
    subtitle: "Le cinéma au meilleur prix",
    backgroundColor: "bg-yellow-500",
    image: promotionCardPathe,
    imageBackground: "bg-yellow-400/30",
    link: "/boutique",
  },
  {
    id: "ikea",
    title: "Cartes IKEA",
    subtitle: "Ameublement et décoration pour votre intérieur",
    backgroundColor: "bg-blue-900",
    image: promotionCardIkea,
    imageBackground: "bg-yellow-500/30",
    link: "/boutique",
  },
  {
    id: "monoprix",
    title: "Cartes Monoprix",
    subtitle: "Achetez vos produits au meilleur prix",
    backgroundColor: "bg-red-900",
    image: promotionCardMonoprix,
    imageBackground: "bg-red-500/30",
    link: "/boutique",
  },
  {
    id: "carrefour",
    title: "Cartes Carrefour",
    subtitle: "Achetez vos produits au meilleur prix",
    backgroundColor: "bg-cyan-900",
    image: promotionCardCarrefour,
    imageBackground: "bg-cyan-500/30",
    link: "/boutique",
  },
  {
    id: "fnac",
    title: "Cartes Fnac",
    subtitle: "Achetez vos produits au meilleur prix",
    backgroundColor: "bg-amber-900",
    image: promotionCardFnac,
    imageBackground: "bg-amber-500/30",
    link: "/boutique",
  },
  {
    id: "sephora",
    title: "Cartes Sephora",
    subtitle: "Achetez vos produits au meilleur prix",
    backgroundColor: "bg-slate-900",
    image: promotionCardSephora,
    imageBackground: "bg-slate-500/30",
    link: "/boutique",
  },
];

export const FRENCH_MONTH_INDEX: Record<string, number> = {
  janvier: 0,
  fevrier: 1,
  février: 1,
  mars: 2,
  avril: 3,
  mai: 4,
  juin: 5,
  juillet: 6,
  aout: 7,
  août: 7,
  septembre: 8,
  octobre: 9,
  novembre: 10,
  decembre: 11,
  décembre: 11,
};

export const MAX_QUANTITY = 200;
export type ParsedTicketSession = {
  code: string
  label: string
  date: Date
  dateKey: string
  timeLabel: string
  displayDate: string
  month: number
  year: number
};

const normalizeMonthLabel = (month: string) =>
  month
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

export const parseSessionLabel = (
  session: { code: string, label: string },
  dateFormatter: Intl.DateTimeFormat,
): ParsedTicketSession | null => {
  const normalizedLabel = session.label
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
  const match = normalizedLabel.match(
    /(\d{1,2})\s+([a-z]+)\s+(\d{4})\s*-\s*(\d{2}:\d{2})/i,
  );

  if (!match) {
    return null;
  }

  const [, dayString, monthLabel, yearString, timeLabel] = match;
  const monthIndex = FRENCH_MONTH_INDEX[normalizeMonthLabel(monthLabel)];

  if (monthIndex === undefined) {
    return null;
  }

  const dayNumber = Number.parseInt(dayString, 10);
  const yearNumber = Number.parseInt(yearString, 10);
  const parsedDate = new Date(yearNumber, monthIndex, dayNumber);
  const isValidDate = Number.isFinite(parsedDate.getTime());

  if (!isValidDate) {
    return null;
  }

  const dateKey = [
    parsedDate.getFullYear(),
    String(parsedDate.getMonth() + 1).padStart(2, "0"),
    String(parsedDate.getDate()).padStart(2, "0"),
  ].join("-");

  return {
    code: session.code,
    label: session.label,
    date: parsedDate,
    dateKey,
    timeLabel,
    displayDate: dateFormatter.format(parsedDate),
    month: parsedDate.getMonth(),
    year: parsedDate.getFullYear(),
  };
};
